/**
 * Shared cart page surfaces, bars, and tokens — use with `paymentUi` for CTAs.
 */
import { alpha } from "@mui/material/styles";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  paymentGradient,
  paymentPrimaryButtonSx,
} from "../payment/paymentUi";

export const CART_MINT = PAYMENT_MINT;
export const CART_MINT_DARK = PAYMENT_MINT_DARK;

export { paymentGradient, paymentPrimaryButtonSx };

/** Full-page background behind cart content */
export const cartPageBackgroundSx = {
  minHeight: "100vh",
  width: "100%",
  background: `linear-gradient(180deg, ${alpha(CART_MINT_DARK, 0.045)} 0%, #f4f8f6 22%, #fafcfb 55%, #ffffff 100%)`,
};

/** Fixed mobile top bar */
export const cartMobileHeaderSx = {
  display: { xs: "flex", sm: "none" },
  alignItems: "center",
  position: "fixed",
  zIndex: 1000,
  width: "100%",
  padding: "10px 10px",
  paddingTop: "max(10px, env(safe-area-inset-top))",
  top: 0,
  left: 0,
  background: alpha("#fff", 0.94),
  backdropFilter: "blur(14px)",
  borderBottom: `1px solid ${alpha(CART_MINT, 0.12)}`,
  boxShadow: `0 10px 32px ${alpha(CART_MINT_DARK, 0.06)}`,
};

/** Desktop sticky checkout column — set `width` / `maxWidth` on the parent Box */
export const cartCheckoutPanelSx = {
  borderRadius: { xs: 0, sm: "20px" },
  p: { xs: 0, sm: 2, md: 2.5 },
  background: {
    xs: "transparent",
    sm: `linear-gradient(165deg, #ffffff 0%, ${alpha(CART_MINT, 0.035)} 55%, #fafcfb 100%)`,
  },
  border: {
    xs: "none",
    sm: `1px solid ${alpha(CART_MINT, 0.14)}`,
  },
  boxShadow: {
    xs: "none",
    sm: `0 20px 48px ${alpha(CART_MINT_DARK, 0.08)}, 0 0 0 1px rgba(255,255,255,0.92) inset`,
  },
};

/** Main order list column — light surface on mobile (horizontal inset comes from cart shell) */
export const cartOrderMainSurfaceSx = {
  borderRadius: { xs: "18px", sm: 0 },
  px: { xs: 0, sm: 0 },
  py: { xs: 1.25, sm: 0 },
  bgcolor: { xs: alpha("#fff", 0.92), sm: "transparent" },
  border: { xs: `1px solid ${alpha(CART_MINT, 0.1)}`, sm: "none" },
  boxShadow: {
    xs: `0 8px 28px ${alpha(CART_MINT_DARK, 0.06)}`,
    sm: "none",
  },
};

/** Tappable row (address) */
export const cartInteractiveRowSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1.5,
  py: 1.75,
  px: { xs: 0.25, sm: 0.5 },
  borderRadius: "14px",
  cursor: "pointer",
  transition: "background 0.18s ease",
  "&:hover": {
    bgcolor: alpha(CART_MINT, 0.06),
  },
};

/** Read-only row (slot preview) */
export const cartPreviewRowSx = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 1.5,
  py: 1.75,
  px: { xs: 0.25, sm: 0.5 },
  borderRadius: "14px",
};

/** Fixed bottom login bar (mobile) */
export const cartMobileLoginBarSx = {
  width: "100%",
  position: "fixed",
  bottom: 0,
  left: 0,
  zIndex: 1010,
  padding: "10px 12px",
  paddingBottom: "max(10px, env(safe-area-inset-bottom))",
  background: alpha("#fff", 0.96),
  backdropFilter: "blur(12px)",
  borderTop: `1px solid ${alpha(CART_MINT, 0.12)}`,
  boxShadow: `0 -12px 36px ${alpha(CART_MINT_DARK, 0.07)}`,
};

/** Sticky pre-checkout bar (mobile) */
export const cartStickyCheckoutBarOuterSx = {
  display: { xs: "flex", sm: "none" },
  position: "fixed",
  left: 0,
  right: 0,
  bottom: "calc(76px + env(safe-area-inset-bottom, 0px))",
  zIndex: 1020,
  flexDirection: "column",
  px: 1,
};

export const cartStickyCheckoutBarInnerSx = {
  borderRadius: "20px",
  p: 1.25,
  background: alpha("#fff", 0.97),
  backdropFilter: "blur(16px)",
  border: `1px solid ${alpha(CART_MINT, 0.16)}`,
  boxShadow: `0 -12px 40px ${alpha(CART_MINT_DARK, 0.1)}, 0 0 0 1px rgba(255,255,255,0.85) inset`,
  maxWidth: "100%",
};

/** Outlined secondary (e.g. view all carts) */
export const cartOutlinedAccentButtonSx = {
  textTransform: "none" as const,
  fontWeight: 700,
  borderRadius: "12px",
  borderWidth: "1.5px",
  borderColor: alpha(CART_MINT, 0.45),
  color: CART_MINT_DARK,
  "&:hover": {
    borderColor: CART_MINT,
    bgcolor: alpha(CART_MINT, 0.06),
  },
};
